import {
  get,
  set,
  has,
  map,
  flow,
  reject,
  groupBy,
  reduce,
  flatMap,
  isEmpty,
  isUndefined
} from 'lodash/fp';
import { Schema } from 'rsuite';

const {
  ArrayType,
  NumberType,
  StringType,
  BooleanType,
  ObjectType
} = Schema.Types;

const ItemSchema = ObjectType().shape({
  name: StringType().isRequired('This field is required'),
  itemId: StringType(),
  photo: StringType(),
  category: StringType(),
  categoryId: StringType(),
  position: ArrayType(),
  healthLevel: StringType(),
  limitRules: ArrayType(),
  isVisible: BooleanType()
});

const CategorySchema = ObjectType().shape({
  id: NumberType(),
  name: StringType().isRequired('This field is required'),
  limitRules: ArrayType(),
  position: NumberType(),
  items: ArrayType(ItemSchema).isRequired('This field is required')
});

export const FormModel = Schema.Model({
  name: StringType()
    .isRequired('This field is required')
    .minLength(2, 'Must be at least 2 characters long')
    .maxLength(120, 'Must not exceed 120 characters long')
    .addRule(
      value => !/<[a-z][\s\S]*>/i.test(value),
      'HTML tags are not allowed'
    ),
  description: StringType()
    .minLength(2, 'Must be at least 2 characters long')
    .maxLength(255, 'Must not exceed 255 characters long')
    .addRule(
      value => !/<[a-z][\s\S]*>/i.test(value),
      'HTML tags are not allowed'
    ),
  notes: StringType()
    .isRequired('This field is required')
    .minLength(2, 'Must be at least 2 characters long')
    .maxLength(255, 'Must not exceed 255 characters long')
    .addRule(
      value => !/<[a-z][\s\S]*>/i.test(value),
      'HTML tags are not allowed'
    ),
  trackingMethodId: NumberType().isRequired('This field is required'),
  limitRules: ObjectType(),
  position: NumberType()
    .min(1, 'Mininum value is 1')
    .max(1000, 'Maximum value is 1000'),
  isVisible: BooleanType().isRequired('This field is required'),
  categories: ArrayType(CategorySchema).isRequired(
    'Must have at least 1 category'
  )
});

export const EmptyCategoryValue = {
  name: '',
  limitRules: null,
  position: 0,
  items: [],
  isVisible: true
};

export const DefaultFormValue = {
  name: '',
  description: '',
  notes:
    'Add any comments, instructions or allergies here to assist in gathering your order.',
  photo: null,
  trackingMethodId: null,
  limitRules: null,
  isVisible: true,
  categories: [],
  items: [],
  position: 1
};

export const picklistToFormValue = picklist => {
  const { trackingMethod, items, ...formValue } = picklist;
  const itemsByCategory = groupBy('category', items);
  const categories = map(
    each => ({ ...each, items: itemsByCategory[each.name] }),
    picklist.categories
  );
  return { ...formValue, categories, trackingMethodId: trackingMethod.id };
};

export const filterNonExistingItemOptions = (itemOptions, categories) => {
  const existingItemsById = flow(
    flatMap(get('items')),
    reject(isUndefined),
    reduce((result, { itemId, name }) => set(itemId, name, result), {})
  )(categories);

  if (isEmpty(existingItemsById)) return itemOptions;

  return reject(({ value }) => has(value, existingItemsById), itemOptions);
};
